/**
 * sqlite3mt push helpers
 *
 * Mechanical extraction from §96 §14.1.
 *
 * This module owns the Control Worker-side push contract:
 * - Push snapshot capture and revalidation (pre-publish and post-publish)
 * - Normalized-delta emptiness check
 * - Spec-shaped pending push base advance installation
 * - The post-busy sync restart epilog (R33)
 *
 * §14.2 runPush() itself is an async orchestration that lives in the
 * Control Worker runtime; this module provides the isolated helpers
 * that make the push state machine testable.
 */

import type { PendingBaseAdvance } from './bootstrap'
import {
    subtractPublishedSnapshotFromLiveWorkspaceState,
    workspaceHasLocalDelta,
} from './dbview'
import { sameRoot, type PublicHead, type Root } from './types'
import type { PageHash, Pgno } from './types'

export {
    subtractPublishedSnapshotFromLiveWorkspaceState,
    workspaceHasLocalDelta,
} from './dbview'

// ── §14.1 Error constants ──────────────────────────────────────

/** Internal: startPendingPushBaseAdvance called while one is already in flight. */
export const INTERNAL_PENDING_BASE_ADVANCE_REENTRY =
    'INTERNAL_PENDING_BASE_ADVANCE_REENTRY'

// ── §14.1 Push snapshot ────────────────────────────────────────

/**
 * Immutable snapshot of workspace state captured at push start.
 *
 * This snapshot is the basis for all revalidation checks during the
 * async push flow. Any mutation of workspace state after snapshot
 * capture invalidates the push unless it is carefully handled.
 */
export interface PushSnapshot {
    expectedRoot: Root
    expectedVersion: bigint
    expectedPageCount: number
    overlaySnapshot: ReadonlyMap<Pgno, PageHash>
}

/**
 * Result of revalidation — true means the snapshot is still valid.
 *
 * §14.1: After each await, push must revalidate that workspace state
 * has not diverged from the snapshot.
 */
export type RevalidationResult = boolean

// ── §14.1 Guards ───────────────────────────────────────────────

/**
 * §14.1: Is a normalized local delta empty after resolution?
 *
 * This covers the case where overlay pages resolve to the same hashes
 * as the base root, meaning there is no effective change to push.
 * Also catches page-count-only changes that revert to the base count.
 */
export function normalizedLocalDeltaIsEmpty(
    normalizedPageHashesSize: number,
    targetPageCount: number,
    baseRootPageCount: number
): boolean {
    return normalizedPageHashesSize === 0
        && targetPageCount === baseRootPageCount
}

// ── §14.1 Revalidation ─────────────────────────────────────────

/**
 * §14.1: Pre-publish revalidation.
 *
 * Checks that workspaceBaseHead and remoteHead still match the snapshot taken
 * at push start. These must match because:
 * - workspaceBaseHead must match: no local base advance happened under us
 * - remoteHead must match: no remote reconcile changed the target
 *
 * Live workspacePageCount is intentionally not a pre-CAS blocker. Newer local
 * commits may grow/truncate the DB while an older snapshot is uploading; after
 * the captured snapshot publishes, base-advance subtraction preserves that
 * newer live page count as residual local work.
 */
export function revalidatePushPrePublishBaseOrReturn(
    snap: PushSnapshot,
    workspaceBaseHead: PublicHead,
    _workspacePageCount: number,
    remoteHead: PublicHead
): RevalidationResult {
    return sameRoot(workspaceBaseHead.root, snap.expectedRoot)
        && workspaceBaseHead.version === snap.expectedVersion
        && sameRoot(remoteHead.root, snap.expectedRoot)
        && remoteHead.version === snap.expectedVersion
}

/**
 * §14.1: Post-publish revalidation (R16).
 *
 * After successful CAS publish, we only need to check that the local
 * workspace base still matches the snapshot. Remote head may have
 * advanced past our published version (which is OK per R16), and
 * workspacePageCount may have changed because a newer local commit raced
 * after publish started. The pending push base advance subtracts the
 * published snapshot from live workspace state and preserves that newer
 * live page count.
 */
export function revalidatePushPostPublishLocalBaseOrReturn(
    snap: PushSnapshot,
    workspaceBaseHead: PublicHead
): RevalidationResult {
    return sameRoot(workspaceBaseHead.root, snap.expectedRoot)
        && workspaceBaseHead.version === snap.expectedVersion
}

// ── §14.1 Pending push base advance ─────────────────────────────

/**
 * §14.1: Start a pending push base advance.
 *
 * Installs the spec-shaped `pendingBaseAdvance.promise`, which performs the
 * local base-head move, subtracts the published snapshot from live workspace
 * state, and then persists the durable snapshot fence.
 *
 * Throws INTERNAL_PENDING_BASE_ADVANCE_REENTRY if called while
 * a base advance is already in flight.
 */
export function startPendingPushBaseAdvance(
    pendingRef: { value: PendingBaseAdvance | null },
    workspaceBaseHeadRef: { value: PublicHead },
    nextHead: PublicHead,
    workspaceOverlay: Map<Pgno, PageHash>,
    workspacePageCountRef: { value: number },
    snapshotOverlay: ReadonlyMap<Pgno, PageHash>,
    snapshotPageCount: number,
    persistSnapshot: () => Promise<void>
): Promise<void> {
    if (pendingRef.value != null) {
        throw new Error(INTERNAL_PENDING_BASE_ADVANCE_REENTRY)
    }

    const promise = (async () => {
        workspaceBaseHeadRef.value = nextHead

        subtractPublishedSnapshotFromLiveWorkspaceState(
            workspaceOverlay,
            workspacePageCountRef,
            snapshotOverlay,
            snapshotPageCount
        )

        await persistSnapshot()
    })()

    pendingRef.value = {
        kind: 'PUSH',
        intentId: null,
        promise,
    }
    return promise
}

// ── §14.1 Post-busy epilog (R33) ────────────────────────────────

/**
 * Post-busy sync restart decision.
 *
 * §14.1 / R33: After pushBusy is cleared, the push flow must check
 * whether a restart is warranted. This prevents stranded state when
 * newer remoteHead or remaining local deltas need another push cycle.
 *
 * Returns true if maybeStartPushOrImport() should be called.
 */
export function shouldRestartSyncAfterPushBusy(
    syncFatalError: unknown | null,
    restartNow: boolean
): boolean {
    return restartNow && syncFatalError == null
}

// ── §14.1 Push snapshot capture ─────────────────────────────────

/**
 * Capture a push snapshot from the current workspace state.
 *
 * This clones the workspace overlay so that concurrent commits
 * during the async push flow don't corrupt the snapshot.
 */
export function capturePushSnapshot(
    workspaceBaseHead: PublicHead,
    workspacePageCount: number,
    workspaceOverlay: ReadonlyMap<Pgno, PageHash>
): PushSnapshot {
    return {
        expectedRoot: workspaceBaseHead.root,
        expectedVersion: workspaceBaseHead.version,
        expectedPageCount: workspacePageCount,
        overlaySnapshot: new Map(workspaceOverlay),
    }
}
